import { Component } from '@theme/component';

const STORAGE_KEY = 'mw-age-confirmed';
const DAY = 24 * 60 * 60 * 1000;

/**
 * Age verification for Mr. Wine. Opens a modal on the first visit and remembers the confirmation
 * on the device (local storage, with a cookie fallback) for the configured number of days.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal.
 * @property {HTMLElement} ask - The question and buttons.
 * @property {HTMLElement} denied - The message shown to under-age visitors.
 *
 * @extends Component<Refs>
 */
class MwAgeGate extends Component {
  requiredRefs = ['dialog', 'ask', 'denied'];

  connectedCallback() {
    super.connectedCallback();
    if (window.Shopify?.designMode || this.#isConfirmed()) return;
    this.refs.dialog.showModal();
  }

  confirm() {
    const until = Date.now() + this.#rememberDays() * DAY;
    try {
      localStorage.setItem(STORAGE_KEY, String(until));
    } catch {
      // Storage can be blocked; the cookie below still remembers the answer.
    }
    document.cookie = `${STORAGE_KEY}=${until}; max-age=${this.#rememberDays() * 86400}; path=/; SameSite=Lax`;
    this.refs.dialog.close();
  }

  deny() {
    this.refs.ask.hidden = true;
    this.refs.denied.hidden = false;
  }

  /** @param {Event} event */
  preventClose(event) {
    event.preventDefault();
  }

  #rememberDays() {
    return Number(this.dataset.rememberDays) || 30;
  }

  #isConfirmed() {
    let until = 0;
    try {
      until = Number(localStorage.getItem(STORAGE_KEY)) || 0;
    } catch {
      until = 0;
    }
    if (!until) {
      const match = document.cookie.match(new RegExp(`(?:^|; )${STORAGE_KEY}=(\\d+)`));
      until = match ? Number(match[1]) : 0;
    }
    return until > Date.now();
  }
}

if (!customElements.get('mw-age-gate')) {
  customElements.define('mw-age-gate', MwAgeGate);
}
